import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Box, Container, Card, Flex, Text, Heading, Button, TextField, Separator, Spinner, Callout } from "@radix-ui/themes";
import { InfoIcon } from "lucide-react";
import { integrationIcons, integrationBrandColors } from "@/components/workflow/IntegrationSelector";
import { config } from "@/config/env";
import toast from "react-hot-toast";

/**
 * Test login page that shows email/password login + social sign-in buttons
 * for all configured social providers on this tenant.
 */
export default function TestLogin() {
  const { orgId } = useParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const apiBase = config.instanceUrl || window.location.origin.replace(":3000", ":7136");

  // Fetch available social providers (public endpoint, no auth needed)
  const { data: socialProviders = [] } = useQuery({
    queryKey: ["social-providers", orgId],
    queryFn: async () => {
      const response = await fetch(`${apiBase}/org/${orgId}/auth/v1/providers`);
      if (!response.ok) return [];
      return response.json();
    },
    enabled: !!orgId,
  });

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${apiBase}/org/${orgId}/auth/v1/token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Login failed");
      }

      const tokens = await response.json();
      if (tokens.access_token) {
        toast.success("Logged in!");
        // In a real app, store tokens and redirect
        window.location.href = `/org/${orgId}/dashboard?access_token=${tokens.access_token}&refresh_token=${tokens.refresh_token}`;
      }
    } catch (err: any) {
      toast.error(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider: string) => {
    try {
      // Apple's form_post POSTs the redirect_uri, so it must hit AnyAPI directly
      // (the SPA can't handle POST). Same-origin path is forwarded to AnyAPI by
      // the Vite dev proxy locally, or by the production reverse proxy in prod.
      const callbackUrl = `${window.location.origin}/org/${orgId}/auth/v1/${provider}/callback`;
      const response = await fetch(
        `${apiBase}/org/${orgId}/auth/v1/${provider}/authorize?redirectUri=${encodeURIComponent(callbackUrl)}`
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to start sign-in");
      }

      const data = await response.json();
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      }
    } catch (err: any) {
      toast.error(err.message || `Failed to start ${provider} sign-in`);
    }
  };

  return (
    <Container size="1" style={{
      minHeight: "100vh", display: "flex", alignItems: "center",
      justifyContent: "center", padding: "1rem",
    }}>
      <Card size="4" style={{ width: "100%", maxWidth: 420 }}>
        <Flex direction="column" gap="4" p="4">
          <Box style={{ textAlign: "center" }}>
            <Heading size="5" mb="2">Sign In</Heading>
            <Text size="2" color="gray">Sign in to your account</Text>
          </Box>

          <Callout.Root size="1" color="blue">
            <Callout.Icon>
              <InfoIcon size={14} />
            </Callout.Icon>
            <Callout.Text style={{ wordBreak: "break-word" }}>
              Tests <strong>email/password and web sign-in only</strong>. Native
              sign-in is tested from your mobile app — see Settings → Integrations
              for the endpoint to post to.
            </Callout.Text>
          </Callout.Root>

          {/* Email/password login */}
          <form onSubmit={handleEmailLogin}>
            <Flex direction="column" gap="3">
              <Box>
                <Text size="2" weight="medium" mb="1">Email</Text>
                <TextField.Root
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </Box>
              <Box>
                <Text size="2" weight="medium" mb="1">Password</Text>
                <TextField.Root
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
              </Box>
              <Button size="3" type="submit" disabled={loading} style={{ cursor: "pointer" }}>
                {loading ? <Spinner size="2" /> : "Sign In"}
              </Button>
            </Flex>
          </form>

          {/* Social sign-in buttons */}
          {socialProviders.length > 0 && (
            <>
              <Flex align="center" gap="3">
                <Separator size="4" style={{ flex: 1 }} />
                <Text size="1" color="gray">or</Text>
                <Separator size="4" style={{ flex: 1 }} />
              </Flex>

              <Flex direction="column" gap="2">
                {socialProviders.map((def: any) => {
                  const Icon = integrationIcons[def.provider];
                  const brand = integrationBrandColors[def.provider];
                  return (
                    <Button
                      key={def.provider}
                      size="3"
                      variant="outline"
                      style={{
                        cursor: "pointer",
                        justifyContent: "center",
                        gap: 8,
                      }}
                      onClick={() => handleSocialLogin(def.provider)}
                    >
                      {Icon && (
                        <Box style={{
                          width: 20, height: 20, borderRadius: 4,
                          backgroundColor: brand?.bg || "var(--gray-9)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          <Box style={{ transform: "scale(0.7)", display: "flex" }}><Icon /></Box>
                        </Box>
                      )}
                      Continue with {def.display_name}
                    </Button>
                  );
                })}
              </Flex>
            </>
          )}
        </Flex>
      </Card>
    </Container>
  );
}
